import React, { useCallback, useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock3,
  Database,
  Loader2,
  RefreshCw,
  Radio,
  Workflow,
  XCircle,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/Card";
import { EntityLinkList } from "../../components/ui/EntityLink";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/Table";
import { adminApi } from "../../utils/api";
import { formatDateTime, formatLlmReplyNotes } from "../../utils/helpers";

const RUN_FETCH_LIMIT = 200;
const RUNS_WITHOUT_SCROLL = 31;
const COST_RUN_BAR_WIDTH = 28;
const SELECTED_STEP_BAR_WIDTH = 110;
const COST_VIEW_OPTIONS = {
  both: {
    label: "Both",
    summaryKey: "estimated_cost_usd",
    chartKey: "totalCost",
    color: "#2563eb",
  },
  openai: {
    label: "OpenAI",
    summaryKey: "openai_cost_usd",
    chartKey: "openaiCost",
    color: "#16a34a",
  },
  brave: {
    label: "Brave",
    summaryKey: "brave_cost_usd",
    chartKey: "braveCost",
    color: "#d97706",
  },
};

function toInputDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseInputDate(value) {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function listDateKeys(startDate, endDate) {
  const start = parseInputDate(startDate);
  const end = parseInputDate(endDate);
  if (!start || !end || start > end) return [];

  const keys = [];
  const current = new Date(start);
  while (current <= end) {
    keys.push(toInputDate(current));
    current.setDate(current.getDate() + 1);
  }
  return keys;
}

function getDefaultRange() {
  const today = new Date();
  return {
    startDate: toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    endDate: toInputDate(today),
  };
}

function formatInteger(value) {
  return Number(value || 0).toLocaleString("en-US");
}

function formatCurrency(value) {
  return `$${Number(value || 0).toFixed(3)}`;
}

function formatDuration(seconds) {
  if (seconds == null) return "-";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function formatShortDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function getCurrencyDomain(values) {
  const maxValue = Math.max(0, ...values.map((value) => Number(value || 0)));
  if (maxValue <= 0) return [0, 1];
  return [0, Number((maxValue * 1.1).toFixed(3))];
}

// One table for the run/step status vocabulary — badge variant and row icon in the same place,
// so a new status is one entry rather than three edits. Amber is reserved for "finished, needs
// attention": `running` used to own it and moves to the neutral blue, because an in-flight run is
// not something to act on.
const RUN_STATUS_META = {
  succeeded: { variant: "success", icon: CheckCircle2, iconClass: "text-green-600" },
  warning: { variant: "warning", icon: AlertTriangle, iconClass: "text-yellow-600" },
  failed: { variant: "destructive", icon: XCircle, iconClass: "text-red-600" },
  running: { variant: "default", icon: Clock3, iconClass: "text-slate-500" },
};
const DEFAULT_STATUS_META = { variant: "secondary", icon: Clock3, iconClass: "text-slate-500" };

function getStatusMeta(status) {
  return RUN_STATUS_META[status] ?? DEFAULT_STATUS_META;
}

function getStatusVariant(status) {
  return getStatusMeta(status).variant;
}

function getStatusLabel(status) {
  if (!status) return "Unknown";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

// The muted "N replies cut off (reason) · N turns at estimated cost (…)" line under a run or a
// step; nothing when there was neither.
function ReplyNotesLine({ metrics, className = "" }) {
  const text = formatLlmReplyNotes(metrics?.incomplete, metrics?.estimated_turns);
  return text ? <p className={`text-xs text-muted-foreground ${className}`.trim()}>{text}</p> : null;
}

function formatWarnings(warnings) {
  return warnings.map((warning) => `${formatInteger(warning.count)} ${warning.label}`).join(" · ");
}

// Which runs the list shows (the cost chart and cards always cover all of them). A manual run is an admin's Classify click
// on one event, artist or venue (#624), or the translation of a description an admin just accepted or edited (#691), not a
// pipeline run: it is labelled, badged and counted apart so the two are never confused.
const RUN_KIND_OPTIONS = {
  all: { label: "All", keep: () => true },
  pipeline: { label: "Pipeline", keep: (run) => run.mode !== "manual" },
  manual: { label: "Manual", keep: (run) => run.mode === "manual" },
};

// A row of buttons picking one key of `options` (each option has a `label`).
function OptionToggle({ options, value, onChange }) {
  return (
    <div className="flex max-w-full overflow-x-auto rounded-md border border-border bg-background p-1">
      {Object.entries(options).map(([key, option]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={`h-8 rounded px-3 text-sm font-medium transition-colors ${
            value === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// What a manual run did, from the API's `manual_trigger`: an accept's translation (#691), an admin's
// Retranslate (#839) or a Classify click (#624).
const MANUAL_ACTIONS = { admin_translate: "Manual translation", admin_retranslate: "Manual retranslation" };

function manualAction(run) {
  return MANUAL_ACTIONS[run.manual_trigger] ?? "Manual classify";
}

function getRunLabel(run) {
  if (!run) return "-";
  if (run.mode === "manual") {
    const ids = run.manual_event_ids || [];
    return ids.length ? `${manualAction(run)}: ${ids.join(", ")}` : manualAction(run);
  }
  if (run.mode === "n8n") return `Execution ${run.execution_id}`;
  if (run.job_count === 1 && run.steps?.[0]?.job_type && run.steps[0].job_type !== "pipeline") {
    return `Admin job: ${run.steps[0].job_type}`;
  }
  return "API fallback";
}

// The selected run's heading: a manual run's event ids link to those events.
function RunLabel({ run }) {
  const ids = run?.mode === "manual" ? run.manual_event_ids || [] : [];
  if (!ids.length) return getRunLabel(run);
  return (
    <>
      {manualAction(run)}:{" "}
      <EntityLinkList type="event" items={ids.map((id) => ({ id }))} />
    </>
  );
}

function buildCostTimelineData(runs, { startDate, endDate, includeEmptyDays }) {
  const grouped = new Map();

  [...runs]
    .sort((a, b) => new Date(a.started_at || a.finished_at || 0) - new Date(b.started_at || b.finished_at || 0))
    .forEach((run) => {
      const groupKey = run.mode === "n8n" && run.execution_id ? `pipeline:${run.execution_id}` : run.run_key;
      const dateValue = run.started_at || run.finished_at;
      if (!dateValue) return;
      const existing = grouped.get(groupKey);
      const summary = run.summary || {};

      if (!existing) {
        const dateKey = toInputDate(new Date(dateValue));
        grouped.set(groupKey, {
          dateKey,
          label: formatShortDate(dateValue),
          fullLabel: run.mode === "n8n" && run.execution_id
            ? `Execution ${run.execution_id} · ${formatDateTime(dateValue)}`
            : formatDateTime(dateValue),
          totalCost: 0,
          openaiCost: 0,
          braveCost: 0,
          runs: [run.run_key],
        });
      }

      const item = grouped.get(groupKey);
      item.totalCost = Number((item.totalCost + Number(summary.estimated_cost_usd || 0)).toFixed(6));
      item.openaiCost = Number((item.openaiCost + Number(summary.openai_cost_usd || 0)).toFixed(6));
      item.braveCost = Number((item.braveCost + Number(summary.brave_cost_usd || 0)).toFixed(6));
      if (!item.runs.includes(run.run_key)) {
        item.runs.push(run.run_key);
      }
    });

  const runItems = Array.from(grouped.values());
  if (!includeEmptyDays) {
    return runItems;
  }

  const runsByDate = runItems.reduce((accumulator, item) => {
    const items = accumulator.get(item.dateKey) || [];
    items.push(item);
    accumulator.set(item.dateKey, items);
    return accumulator;
  }, new Map());

  return listDateKeys(startDate, endDate).flatMap((dateKey) => {
    const items = runsByDate.get(dateKey);
    if (items?.length) return items;

    const date = parseInputDate(dateKey);
    return [
      {
        dateKey,
        label: formatShortDate(date),
        fullLabel: `${formatDateTime(date)} · no runs`,
        totalCost: 0,
        openaiCost: 0,
        braveCost: 0,
        runs: [],
      },
    ];
  });
}

function hasResultJson(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return value !== "";
}

function formatResultJson(value) {
  return JSON.stringify(value, null, 2);
}

function SummaryCard({ title, value, hint, icon: Icon }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

export default function PipelineRunsPage() {
  const defaults = getDefaultRange();
  const [rangeMode, setRangeMode] = useState("current_month");
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [runs, setRuns] = useState([]);
  const [rangeSummary, setRangeSummary] = useState({});
  const [costView, setCostView] = useState("both");
  const [runKind, setRunKind] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRunKey, setSelectedRunKey] = useState(null);

  const loadRuns = useCallback(async () => {
    if (rangeMode !== "total" && startDate && endDate && startDate > endDate) {
      setError("start_date must be before or equal to end_date");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.getPipelineRuns({
        limit: RUN_FETCH_LIMIT,
        startDate: rangeMode === "total" ? null : startDate,
        endDate: rangeMode === "total" ? null : endDate,
      });
      const nextRuns = data.runs || [];
      setRuns(nextRuns);
      setRangeSummary(data.summary || {});
      setSelectedRunKey((current) => {
        if (current && nextRuns.some((run) => run.run_key === current)) {
          return current;
        }
        return nextRuns[0]?.run_key || null;
      });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load pipeline runs.");
    } finally {
      setLoading(false);
    }
  }, [endDate, rangeMode, startDate]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const setCurrentMonthRange = () => {
    const nextDefaults = getDefaultRange();
    setRangeMode("current_month");
    setStartDate(nextDefaults.startDate);
    setEndDate(nextDefaults.endDate);
  };

  const setTotalRange = () => {
    setRangeMode("total");
  };

  const handleStartDateChange = (nextStartDate) => {
    setRangeMode("custom");
    setStartDate(nextStartDate);
    if (endDate && nextStartDate > endDate) {
      setEndDate(nextStartDate);
    }
  };

  const handleEndDateChange = (nextEndDate) => {
    setRangeMode("custom");
    setEndDate(nextEndDate);
  };

  const visibleRuns = runs.filter(RUN_KIND_OPTIONS[runKind].keep);
  const selectedRun = visibleRuns.find((run) => run.run_key === selectedRunKey) || visibleRuns[0] || null;
  const latestRun = runs.find(RUN_KIND_OPTIONS.pipeline.keep) || null;
  const selectedSummary = selectedRun?.summary || {};
  const activeCostView = COST_VIEW_OPTIONS[costView];

  // Every run, manual clicks included: the chart is the range's spend, like the Total Cost card.
  const costTimelineData = buildCostTimelineData(runs, {
    startDate,
    endDate,
    includeEmptyDays: rangeMode !== "total",
  });
  const runChartWidth =
    costTimelineData.length <= RUNS_WITHOUT_SCROLL ? "100%" : costTimelineData.length * COST_RUN_BAR_WIDTH;
  const runCostDomain = getCurrencyDomain(costTimelineData.map((run) => run[activeCostView.chartKey]));

  let cumulativeCost = 0;
  const selectedRunCostChartData =
    selectedRun?.steps?.map((step) => {
      cumulativeCost += Number(step.metrics?.[activeCostView.summaryKey] || 0);
      return {
        label: step.label,
        cumulativeCost: Number(cumulativeCost.toFixed(6)),
      };
    }) || [];
  const selectedRunChartWidth = Math.max(720, selectedRunCostChartData.length * SELECTED_STEP_BAR_WIDTH);
  const selectedRunCostDomain = getCurrencyDomain(selectedRunCostChartData.map((step) => step.cumulativeCost));

  const rangeCutOff = formatLlmReplyNotes(rangeSummary.incomplete, rangeSummary.estimated_turns);
  const rangeLabel =
    rangeMode === "total" ? "All time" : `${startDate || "-"} to ${endDate || "-"}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Pipeline Runs</h2>
          <p className="text-muted-foreground">
            Cost is shown by date range. Default view is the current month from day one until today.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadRuns} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cost Range</CardTitle>
          <CardDescription>Use the current month by default, switch to total, or pick a custom date range.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant={rangeMode === "current_month" ? "default" : "outline"} size="sm" onClick={setCurrentMonthRange}>
              Current Month
            </Button>
            <Button variant={rangeMode === "total" ? "default" : "outline"} size="sm" onClick={setTotalRange}>
              Total
            </Button>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="min-w-0">
              <label className="mb-1 block text-sm font-medium">Start date</label>
              <input
                type="date"
                value={startDate}
                disabled={rangeMode === "total"}
                onChange={(event) => handleStartDateChange(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              />
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-sm font-medium">End date</label>
              <input
                type="date"
                value={endDate}
                disabled={rangeMode === "total"}
                onChange={(event) => handleEndDateChange(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              />
            </div>
            <div className="rounded-md bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Viewing</span>
              <div className="font-medium">{rangeLabel}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-700">{error}</div>
      ) : null}

      {loading && !runs.length ? (
        <Card>
          <CardContent className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : null}

      <div className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold tracking-tight">Range Totals</h3>
          <p className="text-sm text-muted-foreground">Aggregated totals for {rangeLabel}.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            title="Total Cost"
            value={formatCurrency(rangeSummary.estimated_cost_usd)}
            hint={`${formatCurrency(rangeSummary.openai_cost_usd)} OpenAI · ${formatCurrency(rangeSummary.brave_cost_usd)} Brave${
              rangeSummary.manual_run_count
                ? ` · ${formatCurrency(rangeSummary.manual_estimated_cost_usd)} from manual actions`
                : ""
            }${
              rangeSummary.provider_error_count ? ` · ${formatInteger(rangeSummary.provider_error_count)} provider errors` : ""
            }${rangeCutOff ? ` · ${rangeCutOff}` : ""}`}
            icon={BarChart3}
          />
          <SummaryCard
            title="Pipeline Runs"
            value={formatInteger(rangeSummary.run_count)}
            hint={`${formatInteger(rangeSummary.succeeded_runs)} succeeded · ${
              rangeSummary.warning_runs ? `${formatInteger(rangeSummary.warning_runs)} warning · ` : ""
            }${formatInteger(rangeSummary.failed_runs)} failed${
              rangeSummary.manual_run_count ? ` · ${formatInteger(rangeSummary.manual_run_count)} manual` : ""
            }`}
            icon={Activity}
          />
          <SummaryCard
            title="Tokens"
            value={formatInteger(Number(rangeSummary.prompt_tokens || 0) + Number(rangeSummary.completion_tokens || 0))}
            hint={`${formatInteger(rangeSummary.prompt_tokens)} prompt · ${formatInteger(rangeSummary.completion_tokens)} completion`}
            icon={Workflow}
          />
          <SummaryCard
            title="Promoted Events"
            value={formatInteger(rangeSummary.events_activated)}
            hint={`${formatInteger(rangeSummary.duplicates_found)} duplicates found`}
            icon={Database}
          />
          <SummaryCard
            title="JamBase API Calls"
            value={formatInteger(rangeSummary.jambase_api_calls)}
            hint={`Event + artist detail requests incl. retries${
              rangeSummary.jambase_api_call_errors
                ? ` · ${formatInteger(rangeSummary.jambase_api_call_errors)} failed attempts`
                : ""
            }${
              rangeSummary.jambase_fetch_errors
                ? ` · ${formatInteger(rangeSummary.jambase_fetch_errors)} fetch errors`
                : ""
            }`}
            icon={Radio}
          />
        </div>
      </div>

      {!loading && !runs.length ? (
        <Card>
          <CardHeader>
            <CardTitle>No Runs In This Range</CardTitle>
            <CardDescription>Try widening the range or switching to total to inspect older pipeline costs.</CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {runs.length ? (
        <>
          <Card className="min-w-0">
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <CardTitle>Cost By Run</CardTitle>
                  <CardDescription>
                    Showing {activeCostView.label.toLowerCase()} cost for every run in the selected range. Empty days are kept
                    in the timeline so a month stays visually stable.
                  </CardDescription>
                </div>
                <OptionToggle options={COST_VIEW_OPTIONS} value={costView} onChange={setCostView} />
              </div>
            </CardHeader>
            <CardContent className="min-w-0 space-y-6">
              <div className="flex w-full min-w-0">
                <div className="h-[280px] w-[72px] shrink-0 bg-card">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={costTimelineData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                      <YAxis
                        domain={runCostDomain}
                        tickFormatter={(value) => `$${Number(value).toFixed(2)}`}
                        tick={{ fontSize: 12 }}
                        width={70}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain pb-3">
                  <div style={{ minWidth: runChartWidth, width: runChartWidth, height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={costTimelineData} barCategoryGap="18%" margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" minTickGap={18} />
                        <YAxis hide domain={runCostDomain} width={0} />
                        <Tooltip
                          formatter={(value) => [formatCurrency(value), `${activeCostView.label} cost`]}
                          labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel || ""}
                        />
                        <Bar dataKey={activeCostView.chartKey} fill={activeCostView.color} radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className="space-y-3 border-t pt-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="font-medium">Runs In Range</h4>
                    <p className="text-sm text-muted-foreground">Select a run to inspect its node-level stats below.</p>
                  </div>
                  <OptionToggle options={RUN_KIND_OPTIONS} value={runKind} onChange={setRunKind} />
                </div>
                <div className="max-h-[360px] overflow-y-auto rounded-md border">
                  <Table>
                    <TableHeader className="sticky top-0 z-10 bg-card">
                      <TableRow>
                        <TableHead>Run</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Promoted</TableHead>
                        <TableHead>Duplicates</TableHead>
                        <TableHead>Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleRuns.map((run) => {
                        const isSelected = run.run_key === selectedRun?.run_key;
                        const runSummary = run.summary || {};
                        return (
                          <TableRow
                            key={run.run_key}
                            data-state={isSelected ? "selected" : undefined}
                            className={`cursor-pointer ${
                              isSelected ? "border-l-4 border-l-primary bg-muted hover:bg-muted" : "border-l-4 border-l-transparent"
                            }`}
                            onClick={() => setSelectedRunKey(run.run_key)}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {React.createElement(getStatusMeta(run.status).icon, {
                                  className: `h-4 w-4 ${getStatusMeta(run.status).iconClass}`,
                                })}
                                <div>
                                  <p className="flex items-center gap-2 font-medium">
                                    {getRunLabel(run)}
                                    {run.mode === "manual" ? <Badge variant="outline">Manual</Badge> : null}
                                  </p>
                                  <p className="text-xs text-muted-foreground">{run.run_key}</p>
                                  {run.warnings?.length ? (
                                    <p className="text-xs text-yellow-800">{formatWarnings(run.warnings)}</p>
                                  ) : null}
                                  <ReplyNotesLine metrics={runSummary} />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={getStatusVariant(run.status)}>{getStatusLabel(run.status)}</Badge>
                            </TableCell>
                            <TableCell>{formatDateTime(run.started_at)}</TableCell>
                            <TableCell>{formatInteger(runSummary.events_activated)}</TableCell>
                            <TableCell>{formatInteger(runSummary.duplicates_found)}</TableCell>
                            <TableCell>{formatCurrency(runSummary.estimated_cost_usd)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>

          {selectedRun ? (
            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-semibold tracking-tight">Selected Run</h3>
                <p className="text-sm text-muted-foreground">
                  <RunLabel run={selectedRun} /> · {formatDateTime(selectedRun.started_at)}
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <SummaryCard
                  title="Run Status"
                  value={getStatusLabel(selectedRun.status)}
                  hint={`${selectedRun.mode} · ${formatDuration(selectedRun.duration_seconds)}${
                    selectedRun.warning_count ? ` · ${formatInteger(selectedRun.warning_count)} non-blocking errors` : ""
                  }`}
                  icon={getStatusMeta(selectedRun.status).icon}
                />
                <SummaryCard
                  title="Run Cost"
                  value={formatCurrency(selectedSummary.estimated_cost_usd)}
                  hint={`${formatCurrency(selectedSummary.openai_cost_usd)} OpenAI · ${formatCurrency(selectedSummary.brave_cost_usd)} Brave`}
                  icon={BarChart3}
                />
                <SummaryCard
                  title="Promoted Events"
                  value={formatInteger(selectedSummary.events_activated)}
                  hint={`${formatInteger(selectedSummary.duplicates_found)} duplicates found`}
                  icon={Database}
                />
                <SummaryCard
                  title="Tokens"
                  value={formatInteger(Number(selectedSummary.prompt_tokens || 0) + Number(selectedSummary.completion_tokens || 0))}
                  hint={`${formatInteger(selectedSummary.search_calls)} Brave search calls`}
                  icon={Workflow}
                />
              </div>
            </div>
          ) : null}

          <div className="grid gap-6 xl:grid-cols-2">
            <Card className="min-w-0">
              <CardHeader>
                <CardTitle>Selected Run Cost Progression</CardTitle>
                <CardDescription>Step-by-step cumulative {activeCostView.label.toLowerCase()} cost for the selected run.</CardDescription>
              </CardHeader>
              <CardContent className="min-w-0">
                {selectedRunCostChartData.some((step) => step.cumulativeCost > 0) ? (
                  <div className="flex w-full min-w-0">
                    <div className="h-[280px] w-[72px] shrink-0 bg-card">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={selectedRunCostChartData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                          <YAxis
                            domain={selectedRunCostDomain}
                            tickFormatter={(value) => `$${Number(value).toFixed(2)}`}
                            tick={{ fontSize: 12 }}
                            width={70}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain pb-3">
                      <div style={{ minWidth: selectedRunChartWidth, width: selectedRunChartWidth, height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={selectedRunCostChartData} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                            <YAxis hide domain={selectedRunCostDomain} width={0} />
                            <Tooltip formatter={(value) => [formatCurrency(value), `Cumulative ${activeCostView.label.toLowerCase()} cost`]} />
                            <Area
                              type="monotone"
                              dataKey="cumulativeCost"
                              stroke={activeCostView.color}
                              fill={`${activeCostView.color}33`}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
                    No {activeCostView.label.toLowerCase()} cost-bearing steps were recorded for this run.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardHeader>
                <CardTitle>Selected Run Details</CardTitle>
                <CardDescription>
                  {selectedRun
                    ? `${getRunLabel(selectedRun)} · ${formatDateTime(selectedRun.started_at)}`
                    : "Select a run to inspect its cost and processing details."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex flex-col gap-1 rounded-md bg-muted/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-muted-foreground">Run cost</span>
                  <span className="font-medium">{formatCurrency(selectedSummary.estimated_cost_usd)}</span>
                </div>
                <div className="flex flex-col gap-1 rounded-md bg-muted/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-muted-foreground">OpenAI / Brave</span>
                  <span className="font-medium">
                    {formatCurrency(selectedSummary.openai_cost_usd)} / {formatCurrency(selectedSummary.brave_cost_usd)}
                  </span>
                </div>
                <div className="flex flex-col gap-1 rounded-md bg-muted/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-muted-foreground">Prompt / completion</span>
                  <span className="font-medium">
                    {formatInteger(selectedSummary.prompt_tokens)} / {formatInteger(selectedSummary.completion_tokens)}
                  </span>
                </div>
                <div className="flex flex-col gap-1 rounded-md bg-muted/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-muted-foreground">Search calls</span>
                  <span className="font-medium">{formatInteger(selectedSummary.search_calls)}</span>
                </div>
                {/* Every signal that warns is listed here. The rows below are only for counters
                    the warning list deliberately omits, so a number never appears twice. */}
                {selectedRun?.warnings?.map((warning) => (
                  <div
                    key={warning.key}
                    className="flex flex-col gap-1 rounded-md bg-yellow-500/10 px-3 py-2 text-yellow-800 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <span>{warning.label}</span>
                    <span className="font-medium">{formatInteger(warning.count)}</span>
                  </div>
                ))}
                {selectedSummary.jambase_api_call_errors ? (
                  <div className="flex flex-col gap-1 rounded-md bg-muted/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-muted-foreground">JamBase failed attempts (retried)</span>
                    <span className="font-medium">{formatInteger(selectedSummary.jambase_api_call_errors)}</span>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>

          {selectedRun ? (
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle>Node Results For Selected Run</CardTitle>
                  {latestRun?.run_key === selectedRun.run_key ? <Badge variant="outline">Latest In Range</Badge> : null}
                  <Badge variant={getStatusVariant(selectedRun.status)}>{getStatusLabel(selectedRun.status)}</Badge>
                </div>
                <CardDescription>
                  Run key {selectedRun.run_key} · {selectedRun.job_count} jobs · {selectedRun.step_count} nodes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-[520px] overflow-y-auto rounded-md border">
                  <Table>
                    <TableHeader className="sticky top-0 z-10 bg-card">
                      <TableRow>
                        <TableHead>Node</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead className="hidden lg:table-cell lg:min-w-[28rem]">Result JSON</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedRun.steps.map((step, index) => {
                        const resultJson = step.result_json || {};
                        const json = hasResultJson(resultJson) ? formatResultJson(resultJson) : null;
                        const resultPre = json ? (
                          <pre className="mt-2 max-h-72 overflow-auto rounded-md border bg-muted/40 p-3 font-mono text-xs leading-relaxed text-foreground lg:mt-0">
                            {json}
                          </pre>
                        ) : null;
                        return (
                          <React.Fragment key={`${selectedRun.run_key}-${step.step_key}-${index}`}>
                            <TableRow className={resultPre ? "border-b-0 lg:border-b" : undefined}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{step.label}</p>
                                  <p className="text-xs text-muted-foreground">{step.job_type || step.step_key}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant={getStatusVariant(step.status)}>{getStatusLabel(step.status)}</Badge>
                                {step.error ? <p className="mt-1 text-xs text-red-600">{step.error}</p> : null}
                                {step.warnings?.length ? (
                                  <p className="mt-1 text-xs text-yellow-800">{formatWarnings(step.warnings)}</p>
                                ) : null}
                                <ReplyNotesLine metrics={step.metrics} className="mt-1" />
                              </TableCell>
                              <TableCell>{formatDuration(step.duration_seconds)}</TableCell>
                              <TableCell>{formatDateTime(step.started_at)}</TableCell>
                              <TableCell className="hidden lg:table-cell lg:min-w-[28rem] lg:max-w-[52rem]">
                                {resultPre ?? <span className="text-sm text-muted-foreground">No result JSON recorded</span>}
                              </TableCell>
                            </TableRow>
                            {/* Result JSON column is hidden below lg; this row keeps it reachable */}
                            {resultPre ? (
                              <TableRow className="lg:hidden">
                                <TableCell colSpan={4} className="pt-0">
                                  <details>
                                    <summary className="cursor-pointer text-sm text-muted-foreground">Result JSON</summary>
                                    {resultPre}
                                  </details>
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
